import { useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';

import type { SelectedFilterValue } from '../../types';
import { useFilterState } from '../../hooks/useFilterState';

export const CATEGORY_DIALOG_PARAM = 'cd';

export type DialogControls<T> = {
  isOpen: boolean;
  open: (value: T) => void;
  close: () => void;
};

export type SimpleDialogControls = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

export type NestedDialogControlsV2 = {
  filterDialog: DialogControls<never>;
  detailsDialog: DialogControls<string>;
  sortingDialog: DialogControls<string>;
  displayModeDialog: DialogControls<string>;
  categoryDialog: SimpleDialogControls;
  closeAllDialogs: () => void;
};

/** Represents the state and logic of having a two-stage filter selection, selecting a filter in the outer dialog and individual filter values in the inner dialog */
export function useNestedFilterDialogsV2({
  initiallySelectedFilterValues = [],
}: {
  initiallySelectedFilterValues?: SelectedFilterValue[];
} = {}) {
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isSortingDialogOpen, setIsSortingDialogOpen] = useState(false);
  const [isDisplayModeDialogOpen, setIsDisplayModeDialogOpen] = useState(false);

  const searchParams = useSearchParams();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  // Initialize from URL so the dialog stays open when drilling navigates to a new page.
  // After mount, local state drives open/close because shallow URL updates don't reliably
  // re-trigger useSearchParams.
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(
    () => searchParams?.get(CATEGORY_DIALOG_PARAM) === '1',
  );

  const {
    selectedFilterId,
    selectFilter,
    pendingFilterValues,
    toggleFilterValue,
    setFilterRange,
    activeFilterIds,
    hasChanges,
  } = useFilterState(initiallySelectedFilterValues);

  const isDetailsDialogOpen = isFilterDialogOpen && !!selectedFilterId;

  const openDetailsDialog = (id: string) => {
    selectFilter(id);
  };

  const openFilterDialog = () => {
    setIsFilterDialogOpen(true);
  };

  const closeFilterDialog = () => {
    setIsFilterDialogOpen(false);
  };

  const closeDetailsDialog = () => {
    selectFilter(undefined);
  };

  const openSortingDialog = () => {
    setIsSortingDialogOpen(true);
  };

  const closeSortingDialog = () => {
    setIsSortingDialogOpen(false);
  };

  const openCategoryDialog = () => {
    setIsCategoryDialogOpen(true);
    // Keep the URL in sync so a subsequent drilling navigation carries the param forward
    // and the next page mounts with the dialog already open.
    pushQueryChange({ [CATEGORY_DIALOG_PARAM]: '1' }, { shallow: true });
  };

  const closeCategoryDialog = () => {
    setIsCategoryDialogOpen(false);
    pushQueryChange({ [CATEGORY_DIALOG_PARAM]: undefined }, { shallow: true });
  };

  const openDisplayModeDialog = () => {
    setIsDisplayModeDialogOpen(true);
  };

  const closeDisplayModeDialog = () => {
    setIsDisplayModeDialogOpen(false);
  };

  const closeAllDialogs = () => {
    closeDetailsDialog();
    closeFilterDialog();
    closeSortingDialog();
    closeDisplayModeDialog();
    closeCategoryDialog();
  };

  return {
    selectedFilterId,
    activeFilterIds,
    pendingFilterValues,
    hasFilterChanges: hasChanges,
    toggleFilterValue,
    setFilterRange,
    dialogControls: {
      filterDialog: {
        isOpen: isFilterDialogOpen,
        open: openFilterDialog,
        close: closeFilterDialog,
      },
      detailsDialog: {
        isOpen: isDetailsDialogOpen,
        open: openDetailsDialog,
        close: closeDetailsDialog,
      },
      sortingDialog: {
        isOpen: isSortingDialogOpen,
        open: openSortingDialog,
        close: closeSortingDialog,
      },
      categoryDialog: {
        isOpen: isCategoryDialogOpen,
        open: openCategoryDialog,
        close: closeCategoryDialog,
      },
      displayModeDialog: {
        isOpen: isDisplayModeDialogOpen,
        open: openDisplayModeDialog,
        close: closeDisplayModeDialog,
      },
      closeAllDialogs,
    } satisfies NestedDialogControlsV2,
  };
}
